import type { WordInsight } from "../types";

const DB_NAME = "vibetranslations-insights";
const STORE_NAME = "insights";
const DB_VERSION = 1;
const CACHE_VERSION = 3;
const MAX_CACHE_AGE_MS = 7 * 24 * 60 * 60 * 1000;

type InsightCacheEntry = {
  key: string;
  insight: WordInsight;
  updatedAt: number;
};

export type InsightCacheInput = {
  word: string;
  context: string;
  targetLanguageCode: string;
};

const memoryCache = new Map<string, InsightCacheEntry>();

function hash(value: string): string {
  let result = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index);
    result = Math.imul(result, 16777619);
  }
  return (result >>> 0).toString(36);
}

export async function cleanupExpiredInsights(): Promise<void> {
  for (const [key, entry] of memoryCache) {
    if (!isFresh(entry)) memoryCache.delete(key);
  }
  try {
    const database = await openDatabase();
    if (!database) return;
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, "readwrite");
      const request = transaction.objectStore(STORE_NAME).openCursor();
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) return;
        if (!isFresh(cursor.value as InsightCacheEntry)) cursor.delete();
        cursor.continue();
      };
      request.onerror = () => reject(request.error);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
    database.close();
  } catch {
    // Cache cleanup must not prevent reading.
  }
}

export function insightCacheKey(input: InsightCacheInput): string {
  const word = input.word.normalize("NFKC").toLocaleLowerCase();
  const context = input.context.normalize("NFKC").replace(/\s+/g, " ").trim();
  return `v${CACHE_VERSION}:${input.targetLanguageCode}:${hash(`${word}\n${context}`)}`;
}

function isFresh(entry: InsightCacheEntry): boolean {
  return Date.now() - entry.updatedAt <= MAX_CACHE_AGE_MS;
}

function openDatabase(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === "undefined") return Promise.resolve(null);

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME, { keyPath: "key" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function readCachedInsight(
  input: InsightCacheInput,
): Promise<WordInsight | null> {
  const key = insightCacheKey(input);
  const memoryEntry = memoryCache.get(key);
  if (memoryEntry && isFresh(memoryEntry)) return memoryEntry.insight;
  if (memoryEntry) memoryCache.delete(key);

  try {
    const database = await openDatabase();
    if (!database) return null;
    const entry = await new Promise<InsightCacheEntry | undefined>((resolve, reject) => {
      const request = database
        .transaction(STORE_NAME, "readonly")
        .objectStore(STORE_NAME)
        .get(key);
      request.onsuccess = () => resolve(request.result as InsightCacheEntry | undefined);
      request.onerror = () => reject(request.error);
    });
    database.close();

    if (!entry || !isFresh(entry)) return null;
    memoryCache.set(key, entry);
    return entry.insight;
  } catch {
    return null;
  }
}

export async function writeCachedInsight(
  input: InsightCacheInput,
  insight: WordInsight,
): Promise<void> {
  const entry: InsightCacheEntry = {
    key: insightCacheKey(input),
    insight,
    updatedAt: Date.now(),
  };
  memoryCache.set(entry.key, entry);

  try {
    const database = await openDatabase();
    if (!database) return;
    await new Promise<void>((resolve, reject) => {
      const request = database
        .transaction(STORE_NAME, "readwrite")
        .objectStore(STORE_NAME)
        .put(entry);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    database.close();
  } catch {
    // The in-memory cache still keeps this session fast when IndexedDB is unavailable.
  }
}
